/**
 * Object attribute filters — HP + Dig. (map / list toolbar icons) plus optional
 * multi-select **Fáze** (`statuses`) and BUDOVA **Milníky** (`milestones`) for the
 * Objekty list (and any UI that wires them). Matching still accepts legacy dims
 * (fotky, presence milníky…) from older persisted state;
 * `simplifyObjectAttributeFilterState` keeps HP / Dig. / statuses / milestones and clears the rest.
 *
 * HP / Dig. are **count** filters entered as one expression (`>3`, `<10`, `=0`, or bare `3` → eq).
 * Other dims keep presence: `any` | `yes` | `no`. Multi-selects: empty = any (show all).
 */

import {
  BUDOVA_MILESTONE_IDS,
  BUDOVA_MILESTONE_LABELS,
  budovaHasDigitalniCesta,
  parseBudovaMilestonesFromMetadata,
  type BudovaMilestoneId,
} from '../constants/object-workflow/budova-milestone-engine';
import {
  BUDOVA_BUILDING_TYPE_LABELS,
  type BudovaBuildingType,
} from '../constants/object-workflow/budova-building-type.constants';
import { isMsoHp, isVsoHp, resolveBudovaHpCountForPopup } from './budova-hp-display.util';
import { parseWorkflowObjectNotesFromMetadata } from './budova-pripojeni-notes.util';

export type PresenceFilterValue = 'any' | 'yes' | 'no';

export const PRESENCE_FILTER_ANY: PresenceFilterValue = 'any';
export const PRESENCE_FILTER_YES: PresenceFilterValue = 'yes';
export const PRESENCE_FILTER_NO: PresenceFilterValue = 'no';

export const PRESENCE_FILTER_OPTIONS: ReadonlyArray<{
  value: PresenceFilterValue;
  label: string;
}> = [
  { value: 'any', label: 'Libovolné' },
  { value: 'yes', label: 'Ano' },
  { value: 'no', label: 'Ne' },
];

/** Count comparison operators (Czech UI: větší než / menší než / rovno). */
export type CountFilterOp = 'gt' | 'lt' | 'eq';

export interface CountFilterValue {
  /** `null` = filter inactive (any). */
  op: CountFilterOp | null;
  /** Compared value; ignored when `op` is null. */
  value: number | null;
}

export const COUNT_FILTER_OP_OPTIONS: ReadonlyArray<{
  value: CountFilterOp;
  label: string;
  symbol: string;
}> = [
  { value: 'gt', label: 'větší než', symbol: '>' },
  { value: 'lt', label: 'menší než', symbol: '<' },
  { value: 'eq', label: 'rovno', symbol: '=' },
];

export function emptyCountFilter(): CountFilterValue {
  return { op: null, value: null };
}

export function isCountFilterActive(filter: CountFilterValue | null | undefined): boolean {
  if (!filter || filter.op == null || filter.value == null) return false;
  return Number.isFinite(Number(filter.value));
}

/**
 * Normalize persisted / legacy values to a count filter.
 * Legacy presence: `yes` → > 0, `no` → = 0, `any` → inactive.
 */
export function normalizeCountFilter(raw: unknown): CountFilterValue {
  if (raw == null || raw === 'any' || raw === '') return emptyCountFilter();
  if (raw === 'yes') return { op: 'gt', value: 0 };
  if (raw === 'no') return { op: 'eq', value: 0 };
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const opRaw = o['op'];
    const op: CountFilterOp | null =
      opRaw === 'gt' || opRaw === 'lt' || opRaw === 'eq' ? opRaw : null;
    if (!op) return emptyCountFilter();
    const valueRaw = o['value'];
    const n = typeof valueRaw === 'number' ? valueRaw : Number(valueRaw);
    if (!Number.isFinite(n)) return emptyCountFilter();
    return { op, value: Math.trunc(n) };
  }
  return emptyCountFilter();
}

/** Compare a numeric fact (null/missing → 0) against an active count filter. */
export function matchesCountFilter(
  actual: number | null | undefined,
  filter: CountFilterValue | null | undefined,
): boolean {
  if (!isCountFilterActive(filter)) return true;
  const n =
    actual != null && Number.isFinite(Number(actual))
      ? Math.trunc(Number(actual))
      : 0;
  const threshold = Math.trunc(Number(filter!.value));
  switch (filter!.op) {
    case 'gt':
      return n > threshold;
    case 'lt':
      return n < threshold;
    case 'eq':
      return n === threshold;
    default:
      return true;
  }
}

/** Compact badge / input text for toolbar icons, e.g. `>3`, `<10`, `=0`. */
export function formatCountFilterBadge(filter: CountFilterValue | null | undefined): string {
  if (!isCountFilterActive(filter)) return '';
  const opt = COUNT_FILTER_OP_OPTIONS.find((o) => o.value === filter!.op);
  return `${opt?.symbol ?? filter!.op}${Math.trunc(Number(filter!.value))}`;
}

/**
 * Parse a single-field count expression into a count filter.
 *
 * Rules:
 * - Empty / whitespace → inactive
 * - Optional leading `>`, `<`, or `=` (default `=` when omitted)
 * - Remaining digits (optional sign, `,` or `.` decimal) → truncated integer
 * - Operator alone or non-numeric → inactive
 *
 * Examples: `>3`, `<10`, `=0`, `5` (=5), `-1` (=-1).
 */
export function parseCountFilterExpression(raw: string | null | undefined): CountFilterValue {
  if (raw == null) return emptyCountFilter();
  const trimmed = String(raw).trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return emptyCountFilter();

  let op: CountFilterOp = 'eq';
  let numPart = trimmed;
  const first = trimmed.charAt(0);
  if (first === '>' || first === '<' || first === '=') {
    op = first === '>' ? 'gt' : first === '<' ? 'lt' : 'eq';
    numPart = trimmed.slice(1);
  }
  if (!numPart) return emptyCountFilter();

  const n = Number(numPart);
  if (!Number.isFinite(n)) return emptyCountFilter();
  return { op, value: Math.trunc(n) };
}

/** Objekty list URL keys for HP / Dig. count expressions (`?hp=%3E3&dc=%3D0`). */
export const OBJECT_ATTR_FILTER_QUERY_KEYS = {
  hasHp: 'hp',
  digitalniCesta: 'dc',
} as const;

/** Serialize an active count filter to a query value (`>3`, `<10`, `=0`). */
export function countFilterToQueryParam(
  filter: CountFilterValue | null | undefined,
): string | null {
  const badge = formatCountFilterBadge(normalizeCountFilter(filter));
  return badge || null;
}

/** Parse `hp` / `dc` query values into count filters. */
export function parseCountFilterQueryParam(raw: string | null | undefined): CountFilterValue {
  return parseCountFilterExpression(raw);
}

/** Read HP + Dig. filters from list URL query params. */
export function objectAttributeFilterStateFromQuery(params: {
  hp?: string | null;
  dc?: string | null;
}): Pick<ObjectAttributeFilterState, 'hasHp' | 'digitalniCesta'> {
  return {
    hasHp: parseCountFilterQueryParam(params.hp),
    digitalniCesta: parseCountFilterQueryParam(params.dc),
  };
}

/** Write active HP / Dig. filters into list URL query params. */
export function objectAttributeFilterStateToQueryParams(
  state: ObjectAttributeFilterState | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  const hp = countFilterToQueryParam(state?.hasHp);
  if (hp) out[OBJECT_ATTR_FILTER_QUERY_KEYS.hasHp] = hp;
  const dc = countFilterToQueryParam(state?.digitalniCesta);
  if (dc) out[OBJECT_ATTR_FILTER_QUERY_KEYS.digitalniCesta] = dc;
  return out;
}

/** Stable filter dimension ids (URL / prefs / Kotlin parity). */
export const OBJECT_ATTR_FILTER_DIM = {
  digitalniCesta: 'digitalniCesta',
  hasHp: 'hasHp',
  hpKind: 'hpKind',
  hasPhotos: 'hasPhotos',
  hasNotes: 'hasNotes',
  hasMaterials: 'hasMaterials',
  buildingType: 'buildingType',
  milestoneZavrtany: 'milestoneZavrtany',
  milestoneZrizeneVr: 'milestoneZrizeneVr',
  milestoneOdboceno: 'milestoneOdboceno',
  milestoneBabovka: 'milestoneBabovka',
  acquisitionSigned: 'acquisitionSigned',
  status: 'status',
} as const;

export type ObjectAttrFilterDimId =
  (typeof OBJECT_ATTR_FILTER_DIM)[keyof typeof OBJECT_ATTR_FILTER_DIM];

export type ObjectAttrFilterGroupId =
  | 'obsah'
  | 'budova'
  | 'milniky'
  | 'akvizice'
  | 'stav';

export interface ObjectAttrFilterDimDef {
  id: ObjectAttrFilterDimId;
  label: string;
  group: ObjectAttrFilterGroupId;
  kind: 'presence' | 'multi' | 'count';
  /** Object types that show this dimension. Empty = all types. */
  objectTypes: readonly string[];
}

export const OBJECT_ATTR_FILTER_GROUP_LABELS: Record<ObjectAttrFilterGroupId, string> = {
  obsah: 'Obsah',
  budova: 'Budova',
  milniky: 'Milníky',
  akvizice: 'Akvizice',
  stav: 'Stav',
};

/** v1 dimensions — extensible; only relevant dims show for selected object types. */
export const OBJECT_ATTR_FILTER_DIMENSIONS: readonly ObjectAttrFilterDimDef[] = [
  {
    id: OBJECT_ATTR_FILTER_DIM.digitalniCesta,
    label: 'Dig.',
    group: 'budova',
    kind: 'count',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.hasHp,
    label: 'HP',
    group: 'budova',
    kind: 'count',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.hpKind,
    label: 'Typ HP',
    group: 'budova',
    kind: 'multi',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.buildingType,
    label: 'Typ budovy',
    group: 'budova',
    kind: 'multi',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.hasPhotos,
    label: 'Fotky',
    group: 'obsah',
    kind: 'presence',
    objectTypes: [],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.hasNotes,
    label: 'Poznámky',
    group: 'obsah',
    kind: 'presence',
    objectTypes: [],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.hasMaterials,
    label: 'Materiály',
    group: 'obsah',
    kind: 'presence',
    objectTypes: [],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.milestoneZavrtany,
    label: 'Ukončení závrtu',
    group: 'milniky',
    kind: 'presence',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.milestoneZrizeneVr,
    label: 'Zřízené VR',
    group: 'milniky',
    kind: 'presence',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.milestoneOdboceno,
    label: 'Odbočka',
    group: 'milniky',
    kind: 'presence',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.milestoneBabovka,
    label: 'Umístění bábovky',
    group: 'milniky',
    kind: 'presence',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.acquisitionSigned,
    label: 'Podepsaná smlouva',
    group: 'akvizice',
    kind: 'presence',
    objectTypes: ['BUDOVA'],
  },
  {
    id: OBJECT_ATTR_FILTER_DIM.status,
    label: 'Fáze / status',
    group: 'stav',
    kind: 'multi',
    objectTypes: [],
  },
] as const;

/** Objekty-list multi-select options for BUDOVA milestones (completed = match, OR). */
export const OBJECT_ATTR_FILTER_MILESTONE_OPTIONS: ReadonlyArray<{
  value: BudovaMilestoneId;
  label: string;
}> = BUDOVA_MILESTONE_IDS.map((id) => ({
  value: id,
  label: BUDOVA_MILESTONE_LABELS[id],
}));

export const OBJECT_ATTR_FILTER_HP_KIND_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'MSO', label: 'MSO (HP < 3)' },
  { value: 'VSO', label: 'VSO (HP ≥ 3)' },
];

export const OBJECT_ATTR_FILTER_BUILDING_TYPE_UNSET = '__unset__';

export const OBJECT_ATTR_FILTER_BUILDING_TYPE_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  ...(Object.entries(BUDOVA_BUILDING_TYPE_LABELS) as Array<[BudovaBuildingType, string]>).map(
    ([value, label]) => ({ value, label }),
  ),
  { value: OBJECT_ATTR_FILTER_BUILDING_TYPE_UNSET, label: 'Bez typu' },
];

export interface ObjectAttributeFilterState {
  digitalniCesta: CountFilterValue;
  hasHp: CountFilterValue;
  hpKind: string[];
  hasPhotos: PresenceFilterValue;
  hasNotes: PresenceFilterValue;
  hasMaterials: PresenceFilterValue;
  buildingTypes: string[];
  milestoneZavrtany: PresenceFilterValue;
  milestoneZrizeneVr: PresenceFilterValue;
  milestoneOdboceno: PresenceFilterValue;
  milestoneBabovka: PresenceFilterValue;
  acquisitionSigned: PresenceFilterValue;
  /** Phase codes (`WF.PHASE.N`); CSV groups allowed. Empty = any. */
  statuses: string[];
  /**
   * Completed BUDOVA milestone ids (`ZAVRTANY`, …). Empty = any.
   * OR match: building passes if it has completed at least one selected milestone.
   */
  milestones: string[];
}

export function emptyObjectAttributeFilterState(): ObjectAttributeFilterState {
  return {
    digitalniCesta: emptyCountFilter(),
    hasHp: emptyCountFilter(),
    hpKind: [],
    hasPhotos: 'any',
    hasNotes: 'any',
    hasMaterials: 'any',
    buildingTypes: [],
    milestoneZavrtany: 'any',
    milestoneZrizeneVr: 'any',
    milestoneOdboceno: 'any',
    milestoneBabovka: 'any',
    acquisitionSigned: 'any',
    statuses: [],
    milestones: [],
  };
}

/** Expand filter values that may join several phase codes with commas (objects-list parity). */
export function expandObjectAttributeStatusFilterCodes(
  statuses: readonly string[] | null | undefined,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of statuses ?? []) {
    for (const part of String(raw ?? '').split(',')) {
      const code = part.trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      out.push(code);
    }
  }
  return out;
}

/** Cycle presence: any → yes → no → any (legacy toolbar toggles). */
export function cyclePresenceFilterValue(value: PresenceFilterValue): PresenceFilterValue {
  if (value === 'any') return 'yes';
  if (value === 'yes') return 'no';
  return 'any';
}

/**
 * Keep HP + Dig. + Fáze (`statuses`) + Milníky (`milestones`).
 * Clears photos/notes/presence-milestones/etc.
 * Use when emitting from the simplified HP / Dig. icons so phase/milestone
 * multi-selects survive. Also normalizes legacy presence strings to count filters.
 */
export function simplifyObjectAttributeFilterState(
  state: ObjectAttributeFilterState | null | undefined,
): ObjectAttributeFilterState {
  const empty = emptyObjectAttributeFilterState();
  return {
    ...empty,
    digitalniCesta: normalizeCountFilter(state?.digitalniCesta),
    hasHp: normalizeCountFilter(state?.hasHp),
    statuses: [...(state?.statuses ?? [])].map((s) => String(s ?? '').trim()).filter(Boolean),
    milestones: [...(state?.milestones ?? [])].map((s) => String(s ?? '').trim()).filter(Boolean),
  };
}

/** Dimensions exposed in the simplified HP / Dig. UI. */
export const OBJECT_ATTR_FILTER_SIMPLE_DIMS: readonly ObjectAttrFilterDimId[] = [
  OBJECT_ATTR_FILTER_DIM.hasHp,
  OBJECT_ATTR_FILTER_DIM.digitalniCesta,
];

export function isObjectAttributeFilterActive(state: ObjectAttributeFilterState | null | undefined): boolean {
  if (!state) return false;
  const empty = emptyObjectAttributeFilterState();
  return (
    isCountFilterActive(normalizeCountFilter(state.digitalniCesta))
    || isCountFilterActive(normalizeCountFilter(state.hasHp))
    || state.hpKind.length > 0
    || state.hasPhotos !== empty.hasPhotos
    || state.hasNotes !== empty.hasNotes
    || state.hasMaterials !== empty.hasMaterials
    || state.buildingTypes.length > 0
    || state.milestoneZavrtany !== empty.milestoneZavrtany
    || state.milestoneZrizeneVr !== empty.milestoneZrizeneVr
    || state.milestoneOdboceno !== empty.milestoneOdboceno
    || state.milestoneBabovka !== empty.milestoneBabovka
    || state.acquisitionSigned !== empty.acquisitionSigned
    || state.statuses.length > 0
    || (state.milestones?.length ?? 0) > 0
  );
}

export function countActiveObjectAttributeFilters(
  state: ObjectAttributeFilterState | null | undefined,
): number {
  if (!state) return 0;
  let n = 0;
  if (isCountFilterActive(normalizeCountFilter(state.digitalniCesta))) n += 1;
  if (isCountFilterActive(normalizeCountFilter(state.hasHp))) n += 1;
  if (state.hpKind.length > 0) n += 1;
  if (state.hasPhotos !== 'any') n += 1;
  if (state.hasNotes !== 'any') n += 1;
  if (state.hasMaterials !== 'any') n += 1;
  if (state.buildingTypes.length > 0) n += 1;
  if (state.milestoneZavrtany !== 'any') n += 1;
  if (state.milestoneZrizeneVr !== 'any') n += 1;
  if (state.milestoneOdboceno !== 'any') n += 1;
  if (state.milestoneBabovka !== 'any') n += 1;
  if (state.acquisitionSigned !== 'any') n += 1;
  if (state.statuses.length > 0) n += 1;
  if ((state.milestones?.length ?? 0) > 0) n += 1;
  return n;
}

export function dimensionsForObjectTypes(
  selectedTypes: readonly string[] | null | undefined,
): ObjectAttrFilterDimDef[] {
  const types = (selectedTypes ?? []).map((t) => String(t ?? '').trim().toUpperCase()).filter(Boolean);
  // No type selected → show all dims (managers browsing mixed layers).
  if (types.length === 0) return [...OBJECT_ATTR_FILTER_DIMENSIONS];
  return OBJECT_ATTR_FILTER_DIMENSIONS.filter((dim) => {
    if (!dim.objectTypes.length) return true;
    return dim.objectTypes.some((t) => types.includes(t));
  });
}

export interface ObjectAttributeFilterFacts {
  objectType: string;
  status?: string | null;
  digitalniCesta?: number | null;
  hpCountTotal?: number | null;
  photoCount?: number | null;
  /** Explicit API flag; when null/undefined, fall back to metadata notes. */
  hasNotes?: boolean | null;
  hasMaterials?: boolean | null;
  buildingType?: string | null;
  milestoneZavrtany?: boolean | null;
  milestoneZrizeneVr?: boolean | null;
  milestoneOdboceno?: boolean | null;
  milestoneBabovka?: boolean | null;
  milestoneZafouknuto?: boolean | null;
  acquisitionSigned?: boolean | null;
  metadata?: Record<string, unknown> | null;
}

function matchesPresence(filter: PresenceFilterValue, present: boolean): boolean {
  if (filter === 'any') return true;
  return filter === 'yes' ? present : !present;
}

/** Prefer explicit API flag; otherwise parse notes from metadata. */
function resolveHasNotes(facts: ObjectAttributeFilterFacts): boolean {
  if (typeof facts.hasNotes === 'boolean') return facts.hasNotes;
  return parseWorkflowObjectNotesFromMetadata(facts.metadata ?? null, facts.objectType).length > 0;
}

function resolveMilestones(facts: ObjectAttributeFilterFacts): {
  zavrtany: boolean;
  zrizeneVr: boolean;
  odboceno: boolean;
  postavenaBabovka: boolean;
  zafouknuto: boolean;
} {
  const fromMeta = parseBudovaMilestonesFromMetadata(facts.metadata ?? undefined);
  return {
    zavrtany: facts.milestoneZavrtany ?? fromMeta.zavrtany === true,
    zrizeneVr: facts.milestoneZrizeneVr ?? fromMeta.zrizeneVr === true,
    odboceno: facts.milestoneOdboceno ?? fromMeta.odboceno === true,
    postavenaBabovka: facts.milestoneBabovka ?? fromMeta.postavenaBabovka === true,
    zafouknuto: facts.milestoneZafouknuto ?? fromMeta.zafouknuto === true,
  };
}

function isCompletedBudovaMilestone(
  milestoneId: string,
  ms: ReturnType<typeof resolveMilestones>,
): boolean {
  switch (milestoneId.trim().toUpperCase()) {
    case 'ZAVRTANY':
      return ms.zavrtany;
    case 'ZRIZENE_VR':
      return ms.zrizeneVr;
    case 'ODBOCENO':
      return ms.odboceno;
    case 'POSTAVENA_BABOVKA':
      return ms.postavenaBabovka;
    case 'ZAFOUKNUTO':
      return ms.zafouknuto;
    default:
      return false;
  }
}

/**
 * True when the selected phase codes apply to this object type
 * (`VYKOP.1.PHASE.0` → VYKOP, `BUDOVA.2.PHASE.1` → BUDOVA).
 * Other types stay visible when the phase filter only targets a different type.
 */
export function objectAttributeStatusFilterAppliesToType(
  objectType: string,
  statusCodes: readonly string[],
): boolean {
  const type = String(objectType ?? '').trim().toUpperCase();
  if (!type || statusCodes.length === 0) return false;
  const prefix = `${type}.`;
  return statusCodes.some((code) => {
    const c = String(code ?? '').trim().toUpperCase();
    return c.startsWith(prefix);
  });
}

export function objectMatchesAttributeFilter(
  facts: ObjectAttributeFilterFacts,
  state: ObjectAttributeFilterState | null | undefined,
): boolean {
  if (!state || !isObjectAttributeFilterActive(state)) return true;

  const type = String(facts.objectType ?? '').trim().toUpperCase();
  const isBudova = type === 'BUDOVA';

  if (state.hasPhotos !== 'any') {
    const photos = Math.max(0, Math.trunc(Number(facts.photoCount ?? 0)) || 0);
    if (!matchesPresence(state.hasPhotos, photos > 0)) return false;
  }

  if (state.hasNotes !== 'any') {
    if (!matchesPresence(state.hasNotes, resolveHasNotes(facts))) return false;
  }

  if (state.hasMaterials !== 'any') {
    const hasMat = facts.hasMaterials === true;
    // Unknown materials → fail closed for "yes", pass for "no" only when explicitly false.
    if (state.hasMaterials === 'yes' && facts.hasMaterials == null) return false;
    if (state.hasMaterials === 'no' && facts.hasMaterials == null) return true;
    if (!matchesPresence(state.hasMaterials, hasMat)) return false;
  }

  if (state.statuses.length > 0) {
    const allowed = expandObjectAttributeStatusFilterCodes(state.statuses);
    if (
      allowed.length > 0
      && objectAttributeStatusFilterAppliesToType(type, allowed)
    ) {
      const status = String(facts.status ?? '').trim();
      if (!status || !allowed.includes(status)) return false;
    }
  }

  if (!isBudova) {
    // Non-BUDOVA: ignore budova-only dims (including milestones multi-select).
    return true;
  }

  if ((state.milestones?.length ?? 0) > 0) {
    const ms = resolveMilestones(facts);
    const anyComplete = state.milestones.some((id) => isCompletedBudovaMilestone(id, ms));
    if (!anyComplete) return false;
  }

  const dcFilter = normalizeCountFilter(state.digitalniCesta);
  if (isCountFilterActive(dcFilter)) {
    const dc =
      facts.digitalniCesta != null && Number.isFinite(Number(facts.digitalniCesta))
        ? Math.trunc(Number(facts.digitalniCesta))
        : 0;
    if (!matchesCountFilter(dc, dcFilter)) return false;
  }

  const hp = facts.hpCountTotal != null && Number.isFinite(Number(facts.hpCountTotal))
    ? Math.trunc(Number(facts.hpCountTotal))
    : null;
  const hasHp = hp != null && hp > 0;

  const hpFilter = normalizeCountFilter(state.hasHp);
  if (isCountFilterActive(hpFilter)) {
    if (!matchesCountFilter(hp ?? 0, hpFilter)) return false;
  }

  if (state.hpKind.length > 0) {
    if (!hasHp) return false;
    const kind = isVsoHp(hp!) ? 'VSO' : isMsoHp(hp!) ? 'MSO' : null;
    if (!kind || !state.hpKind.includes(kind)) return false;
  }

  if (state.buildingTypes.length > 0) {
    const bt = (facts.buildingType ?? '').trim() || OBJECT_ATTR_FILTER_BUILDING_TYPE_UNSET;
    if (!state.buildingTypes.includes(bt)) return false;
  }

  const ms = resolveMilestones(facts);
  if (state.milestoneZavrtany !== 'any' && !matchesPresence(state.milestoneZavrtany, ms.zavrtany)) {
    return false;
  }
  if (state.milestoneZrizeneVr !== 'any' && !matchesPresence(state.milestoneZrizeneVr, ms.zrizeneVr)) {
    return false;
  }
  if (state.milestoneOdboceno !== 'any' && !matchesPresence(state.milestoneOdboceno, ms.odboceno)) {
    return false;
  }
  if (state.milestoneBabovka !== 'any' && !matchesPresence(state.milestoneBabovka, ms.postavenaBabovka)) {
    return false;
  }

  if (state.acquisitionSigned !== 'any') {
    if (facts.acquisitionSigned == null) {
      if (state.acquisitionSigned === 'yes') return false;
    } else if (!matchesPresence(state.acquisitionSigned, facts.acquisitionSigned === true)) {
      return false;
    }
  }

  return true;
}

/** Integer HP from API numbers or numeric strings; non-integers stay unset. */
function finiteIntegerCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isInteger(n)) return n;
  }
  return null;
}

/** Extract filter facts from a map/list object-shaped payload. */
export function extractObjectAttributeFilterFacts(obj: {
  type?: string | null;
  status?: string | null;
  digitalniCesta?: number | null;
  photoCount?: number | null;
  hasNotes?: boolean | null;
  hasMaterials?: boolean | null;
  hpCountTotal?: number | null;
  /** Acquisition import HP (`object_acquisition.hp_count`) — popup fallback when spec is 0. */
  acquisitionHpCount?: number | null;
  budovaBuildingType?: string | null;
  acquisitionSigned?: boolean | null;
  metadata?: unknown;
  specSummary?: { hpCountTotal?: number | null } | null;
  spec?: { hpCountTotal?: number | null; digitalniCesta?: number | null } | null;
  acquisition?: { hpCount?: number | null } | null;
}): ObjectAttributeFilterFacts {
  const metadata =
    obj.metadata && typeof obj.metadata === 'object' && !Array.isArray(obj.metadata)
      ? (obj.metadata as Record<string, unknown>)
      : null;
  // Same order as the building popup: any spec HP > 0, else summary, else acquisition import.
  // Spec row `hp_count_total = 0` is an empty placeholder, not "this building has 0 HP".
  const directHp = finiteIntegerCount(obj.hpCountTotal);
  const specHp = finiteIntegerCount(obj.spec?.hpCountTotal);
  const summaryHp = finiteIntegerCount(obj.specSummary?.hpCountTotal);
  const positiveHp = [directHp, specHp, summaryHp].find((n) => n != null && n > 0);
  const hp = positiveHp != null
    ? positiveHp
    : resolveBudovaHpCountForPopup({
        specHpTotal: directHp ?? specHp,
        specSummaryHpTotal: summaryHp,
        acquisitionHpCount:
          finiteIntegerCount(obj.acquisitionHpCount)
          ?? finiteIntegerCount(obj.acquisition?.hpCount),
      });
  const digitalniCesta =
    finiteIntegerCount(obj.digitalniCesta)
    ?? finiteIntegerCount(obj.spec?.digitalniCesta);
  return {
    objectType: String(obj.type ?? '').trim().toUpperCase(),
    status: obj.status ?? null,
    digitalniCesta,
    hpCountTotal: hp,
    photoCount: obj.photoCount ?? 0,
    hasNotes: obj.hasNotes,
    hasMaterials: obj.hasMaterials,
    buildingType: obj.budovaBuildingType ?? null,
    acquisitionSigned: obj.acquisitionSigned ?? null,
    metadata,
  };
}

/** True when BUDOVA should show a missing-DC affordance (typed Připojená without DC). */
export function budovaMissingDigitalniCestaAffordance(facts: {
  objectType?: string | null;
  buildingType?: string | null;
  digitalniCesta?: number | null;
}): boolean {
  const type = String(facts.objectType ?? '').trim().toUpperCase();
  if (type !== 'BUDOVA') return false;
  const bt = String(facts.buildingType ?? '').trim().toUpperCase();
  if (bt !== 'PRIPOJENA') return false;
  return !budovaHasDigitalniCesta(facts.digitalniCesta);
}
